'use client';

import React, { useMemo } from 'react';
import {
	Building2, Calendar, Clock, DollarSign, ExternalLink,
	FileText, Gavel, Landmark, Receipt, Scale, Shield,
	User,
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { useTheme } from '@/contexts/ThemeContext';
import DocViewerOverviewTab from './DocViewerOverviewTab';

interface LegalOverviewTabProps {
	workItem: WorkItem;
	formData: Record<string, any>;
	isDark?: boolean;
	workItemId?: string;
	onOpenItem?: (itemId: string) => void;
}

interface DetailItem {
	label: string;
	value: React.ReactNode;
	icon?: React.ElementType;
}

interface DetailGroup {
	title: string;
	icon: React.ElementType;
	items: DetailItem[];
}

function SectionCard({
	title,
	icon: Icon,
	isDark,
	subtitle,
	children,
}: {
	title: string;
	icon: React.ElementType;
	isDark: boolean;
	subtitle?: string;
	children: React.ReactNode;
}) {
	return (
		<div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
			<div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
				<div className="flex items-center gap-2">
					<Icon className="w-4 h-4 text-blue-500" />
					<div>
						<h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{title}</h3>
						{subtitle && (
							<p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>
						)}
					</div>
				</div>
			</div>
			<div className="p-4">{children}</div>
		</div>
	);
}

function FactCard({
	label,
	value,
	icon: Icon,
	isDark,
}: {
	label: string;
	value: React.ReactNode;
	icon: React.ElementType;
	isDark: boolean;
}) {
	return (
		<div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
			<div className="flex items-center justify-between gap-2 mb-1">
				<span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
				<Icon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
			</div>
			<div className={`text-sm font-semibold break-words ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{value || '—'}</div>
		</div>
	);
}

function DetailGrid({ group, isDark }: { group: DetailGroup; isDark: boolean }) {
	const Icon = group.icon;

	return (
		<SectionCard title={group.title} icon={Icon} isDark={isDark}>
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
				{group.items.map((item) => {
					const ItemIcon = item.icon;
					return (
						<div key={`${group.title}-${item.label}`} className={`p-3 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
							<div className="flex items-center gap-2 mb-1">
								{ItemIcon && <ItemIcon className={`w-3.5 h-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />}
								<span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</span>
							</div>
							<div className={`text-sm break-words ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{item.value || '—'}</div>
						</div>
					);
				})}
			</div>
		</SectionCard>
	);
}

function safeCustomFields(workItem: WorkItem): Record<string, any> {
	if (!workItem.custom_fields) return {};
	if (typeof workItem.custom_fields === 'string') {
		try {
			return JSON.parse(workItem.custom_fields);
		} catch {
			return {};
		}
	}

	return workItem.custom_fields as Record<string, any>;
}

function titleCase(value?: string | null): string {
	if (!value) return '—';
	return value
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value?: string | null, withTime = false): string {
	if (!value) return '—';

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '—';

	return date.toLocaleString('en-ZA', withTime
		? { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
		: { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(value?: number | string | null): string {
	if (value === null || value === undefined || value === '') return '—';
	const numericValue = typeof value === 'number' ? value : Number(value);
	if (Number.isNaN(numericValue)) return '—';

	return new Intl.NumberFormat('en-ZA', {
		style: 'currency',
		currency: 'ZAR',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(numericValue);
}

function getLegalType(workItem: WorkItem, customFields: Record<string, any>): string {
	return (customFields.legal_item_type || workItem.item_type || '').toLowerCase();
}

export default function LegalOverviewTab({
	workItem,
	formData,
	isDark: isDarkProp,
	workItemId,
	onOpenItem,
}: LegalOverviewTabProps) {
	const { currentTheme } = useTheme();
	const isDark = isDarkProp ?? currentTheme.mode === 'dark';
	const customFields = useMemo(() => safeCustomFields(workItem), [workItem]);
	const legalType = useMemo(() => getLegalType(workItem, customFields), [workItem, customFields]);
	const effectiveId = workItemId || workItem.id;

	const title = formData.title || workItem.title || 'Legal Item';
	const description = formData.description || workItem.description || customFields.preparation_notes || customFields.scope_of_work || '';
	const referenceNumber = customFields.matter_number || customFields.reference_number || customFields.court_case_number || workItem.item_number || '—';
	const relatedMatterId = customFields.matter_id || workItem.parent_id || null;
	const relatedMatterLabel = customFields.matter_title || customFields.related_matter_title || null;
	const statusLabel = titleCase(customFields.contract_status || formData.status || workItem.status);
	const priorityLabel = workItem.priority ? titleCase(workItem.priority) : null;

	const typeMeta = {
		matter: { label: 'Matter', icon: Scale, badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
		contract: { label: 'Contract', icon: FileText, badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
		court_date: { label: 'Court Date', icon: Gavel, badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
		trust_transaction: { label: 'Trust Transaction', icon: Landmark, badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
	}[legalType] || { label: titleCase(legalType), icon: Scale, badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };

	const topFacts = useMemo(() => {
		switch (legalType) {
			case 'matter':
				return [
					{ label: 'Client', value: customFields.client_name || workItem.contact_name || '—', icon: Building2 },
					{ label: 'Lead Attorney', value: customFields.lead_attorney || workItem.assigned_to_name || '—', icon: User },
					{ label: 'Practice Area', value: titleCase(customFields.practice_area || workItem.item_subtype || 'general'), icon: Scale },
					{ label: 'Retainer', value: formatCurrency(customFields.retainer_amount), icon: DollarSign },
				];
			case 'contract':
				return [
					{ label: 'Contract Status', value: titleCase(customFields.contract_status || workItem.status), icon: FileText },
					{ label: 'Template', value: customFields.template_id || 'Custom / Uploaded', icon: Receipt },
					{ label: 'Version', value: customFields.version || '1', icon: Clock },
					{ label: 'File Format', value: (customFields.file_extension || customFields.source_extension || '').replace(/^\./, '').toUpperCase() || 'Attachment', icon: FileText },
				];
			case 'court_date':
				return [
					{ label: 'Scheduled For', value: formatDate(workItem.due_date || workItem.scheduled_start || workItem.scheduled_end, true), icon: Calendar },
					{ label: 'Hearing Type', value: customFields.hearing_type || titleCase(workItem.item_subtype || 'court date'), icon: Gavel },
					{ label: 'Court', value: customFields.court_name || '—', icon: Building2 },
					{ label: 'Judge', value: customFields.judge || '—', icon: Shield },
				];
			case 'trust_transaction':
				return [
					{ label: 'Transaction Type', value: titleCase(customFields.transaction_type || workItem.item_subtype || workItem.status), icon: Landmark },
					{ label: 'Amount', value: formatCurrency(customFields.amount || workItem.actual_cost), icon: DollarSign },
					{ label: 'Balance After', value: formatCurrency(customFields.balance_after), icon: Receipt },
					{ label: 'Posted By', value: customFields.created_by_name || workItem.assigned_to_name || '—', icon: User },
				];
			default:
				return [
					{ label: 'Status', value: statusLabel, icon: Shield },
					{ label: 'Reference', value: referenceNumber, icon: Receipt },
					{ label: 'Created', value: formatDate(workItem.created_at), icon: Calendar },
					{ label: 'Updated', value: formatDate(workItem.updated_at), icon: Clock },
				];
		}
	}, [customFields, legalType, referenceNumber, statusLabel, workItem]);

	const detailGroups = useMemo<DetailGroup[]>(() => {
		switch (legalType) {
			case 'matter':
				return [
					{
						title: 'Matter & Parties',
						icon: Scale,
						items: [
							{ label: 'Reference', value: referenceNumber, icon: Receipt },
							{ label: 'Client', value: customFields.client_name || workItem.contact_name || '—', icon: Building2 },
							{ label: 'Opposing Party', value: customFields.opposing_party || '—', icon: Building2 },
							{ label: 'Opposing Counsel', value: customFields.opposing_counsel || '—', icon: User },
						],
					},
					{
						title: 'Case Management',
						icon: Shield,
						items: [
							{ label: 'Billing Type', value: titleCase(customFields.billing_type || 'hourly'), icon: DollarSign },
							{ label: 'Jurisdiction', value: customFields.jurisdiction || '—', icon: Scale },
							{ label: 'Case Number', value: customFields.court_case_number || '—', icon: Receipt },
							{ label: 'Conflict Check', value: customFields.conflict_check === true || customFields.conflict_check_complete === true ? 'Complete' : 'Pending', icon: Shield },
							{ label: 'Statute of Limitations', value: formatDate(customFields.statute_of_limitations), icon: Calendar },
							{ label: 'Due Date', value: formatDate(workItem.due_date), icon: Clock },
						],
					},
				];
			case 'contract':
				return [
					{
						title: 'Contract Metadata',
						icon: FileText,
						items: [
							{ label: 'Reference', value: referenceNumber, icon: Receipt },
							{ label: 'Generated By', value: customFields.generated_by || customFields.created_by_name || workItem.assigned_to_name || '—', icon: User },
							{ label: 'AI Provider', value: titleCase(customFields.ai_provider || 'manual'), icon: Shield },
							{ label: 'Generated At', value: formatDate(workItem.created_at, true), icon: Calendar },
						],
					},
					{
						title: 'Matter Linkage',
						icon: Scale,
						items: [
							{ label: 'Related Matter', value: relatedMatterLabel || relatedMatterId || '—', icon: Scale },
							{ label: 'Matter ID', value: relatedMatterId || '—', icon: Receipt },
							{ label: 'Storage Token', value: customFields.storage_token || '—', icon: Shield },
							{ label: 'Current File URL', value: customFields.file_url || '—', icon: ExternalLink },
						],
					},
				];
			case 'court_date':
				return [
					{
						title: 'Hearing Logistics',
						icon: Gavel,
						items: [
							{ label: 'Matter', value: relatedMatterLabel || customFields.matter_title || '—', icon: Scale },
							{ label: 'Courtroom', value: customFields.courtroom || '—', icon: Building2 },
							{ label: 'Assigned Attorney', value: workItem.assigned_to_name || customFields.assigned_to_name || '—', icon: User },
							{ label: 'Due / Hearing Date', value: formatDate(workItem.due_date || workItem.scheduled_start || workItem.scheduled_end, true), icon: Calendar },
						],
					},
					{
						title: 'Preparation',
						icon: Shield,
						items: [
							{ label: 'Status', value: statusLabel, icon: Shield },
							{ label: 'Priority', value: priorityLabel || '—', icon: Clock },
							{ label: 'Notes', value: customFields.preparation_notes || workItem.description || '—', icon: FileText },
							{ label: 'Last Updated', value: formatDate(workItem.updated_at, true), icon: Clock },
						],
					},
				];
			case 'trust_transaction':
				return [
					{
						title: 'Transaction Details',
						icon: Landmark,
						items: [
							{ label: 'Reference', value: referenceNumber, icon: Receipt },
							{ label: 'Matter', value: relatedMatterLabel || customFields.matter_title || '—', icon: Scale },
							{ label: 'Posted At', value: formatDate(workItem.created_at, true), icon: Calendar },
							{ label: 'Status', value: statusLabel, icon: Shield },
						],
					},
					{
						title: 'Audit Trail',
						icon: Shield,
						items: [
							{ label: 'Created By', value: customFields.created_by_name || workItem.assigned_to_name || '—', icon: User },
							{ label: 'Matter ID', value: relatedMatterId || '—', icon: Receipt },
							{ label: 'Description', value: workItem.description || workItem.title || '—', icon: FileText },
							{ label: 'Updated', value: formatDate(workItem.updated_at, true), icon: Clock },
						],
					},
				];
			default:
				return [
					{
						title: 'Legal Item Details',
						icon: Scale,
						items: [
							{ label: 'Reference', value: referenceNumber, icon: Receipt },
							{ label: 'Status', value: statusLabel, icon: Shield },
							{ label: 'Created', value: formatDate(workItem.created_at, true), icon: Calendar },
							{ label: 'Updated', value: formatDate(workItem.updated_at, true), icon: Clock },
						],
					},
				];
		}
	}, [customFields, legalType, priorityLabel, referenceNumber, relatedMatterId, relatedMatterLabel, statusLabel, workItem]);

	const TypeIcon = typeMeta.icon;

	return (
		<div className="space-y-4">
			<SectionCard
				title={`${typeMeta.label} Summary`}
				icon={TypeIcon}
				isDark={isDark}
				subtitle="Purpose-built legal overview with inline document access"
			>
				<div className="space-y-4">
					<div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-2 flex-wrap mb-2">
								<span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${typeMeta.badge}`}>{typeMeta.label}</span>
								<span className={`px-2.5 py-1 text-xs font-medium rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{statusLabel}</span>
								{priorityLabel && priorityLabel !== '—' && (
									<span className={`px-2.5 py-1 text-xs font-medium rounded-full ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>{priorityLabel}</span>
								)}
								{(customFields.conflict_check === true || customFields.conflict_check_complete === true) && legalType === 'matter' && (
									<span className={`px-2.5 py-1 text-xs font-medium rounded-full ${isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
										Conflict Check Complete
									</span>
								)}
							</div>

							<h2 className={`text-xl font-bold break-words ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{title}</h2>
							<div className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
								{referenceNumber !== '—' && <span className="font-mono">{referenceNumber}</span>}
								{workItem.item_number && referenceNumber !== workItem.item_number && (
									<span className="font-mono"> · {workItem.item_number}</span>
								)}
							</div>

							{description && (
								<p className={`mt-3 text-sm leading-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{description}</p>
							)}
						</div>

						{(relatedMatterId || relatedMatterLabel) && legalType !== 'matter' && onOpenItem && (
							<button
								type="button"
								onClick={() => relatedMatterId && onOpenItem(relatedMatterId)}
								className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${isDark ? 'bg-gray-900 border-gray-700 text-gray-200 hover:bg-gray-800' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-white'}`}
							>
								<Scale className="w-4 h-4 text-blue-500" />
								<span className="truncate max-w-[220px]">{relatedMatterLabel || 'Open linked matter'}</span>
								<ExternalLink className="w-3.5 h-3.5" />
							</button>
						)}
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
						{topFacts.map((fact) => (
							<FactCard
								key={fact.label}
								label={fact.label}
								value={fact.value}
								icon={fact.icon}
								isDark={isDark}
							/>
						))}
					</div>
				</div>
			</SectionCard>

			<SectionCard
				title="Document Workspace"
				icon={FileText}
				isDark={isDark}
				subtitle="Preview linked contracts, pleadings, evidence, and other attached files inline"
			>
				<DocViewerOverviewTab
					workItemId={effectiveId}
					isActive={true}
					isDark={isDark}
					viewerHeight={legalType === 'contract' ? '460px' : '400px'}
				/>
			</SectionCard>

			<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
				{detailGroups.map((group) => (
					<DetailGrid key={group.title} group={group} isDark={isDark} />
				))}
			</div>
		</div>
	);
}
